<script setup lang="ts">
import { computed, ref, useId } from 'vue';
import { t } from '@/i18n';

/*
 * A labelled text input with its hint, its error, and the aria wiring that ties the three
 * together. Extracted rather than repeated because CreateGameView alone has seven fields, and
 * getting `aria-describedby` wrong seven times by hand is the bug this prevents.
 *
 * The input classes are the canonical ones — see apps/web/docs/DESIGN_SYSTEM.md. They live here
 * rather than in main.css because they are all utilities; the @layer components rules exist for
 * things utilities express badly, which an input is not.
 */

const props = defineProps<{
  name: string;
  label: string;
  modelValue: string;
  hint?: string | undefined;
  /**
   * Put the hint behind a "?" beside the label instead of under it.
   *
   * For a hint most people never need — the price field's, where 0 is a whole mode almost nobody
   * will choose — so the form stays short for the majority without the minority having to guess.
   * The hint itself is never *removed*: it stays in `aria-describedby`, visually hidden, so
   * somebody using a screen reader is told without having to find a button first.
   */
  hintTooltip?: boolean | undefined;
  /**
   * `| undefined` explicitly, and every optional prop below is the same.
   *
   * Under `exactOptionalPropertyTypes` — which the shared preset turns on —
   * `error?: string` means "may be absent", *not* "may be undefined". Every caller here binds
   * `:error="errors.teamA"` off a `Partial<…>` record, which is `string | undefined`, so the flag
   * refuses it. Widening the prop is the honest fix: absent and explicitly-undefined mean the same
   * thing to this component, and saying so is what makes the binding legal.
   */
  error?: string | undefined;
  type?: string | undefined;
  /** The literal union the DOM accepts, not a bare string — vue-tsc checks the binding. */
  inputmode?:
    'text' | 'numeric' | 'email' | 'tel' | 'url' | 'search' | 'decimal' | 'none' | undefined;
  autocomplete?: string | undefined;
  /**
   * What the phone's Return key should say — `go`, `send`, `done`, `next`, `search`.
   *
   * It was set nowhere, so every keyboard offered a generic Return: the join screen's code field,
   * the contact form and the six-digit code are each the last field before a submit, and each of
   * them is a different promise about what pressing it does.
   */
  enterkeyhint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send' | undefined;
  maxlength?: number | undefined;
  placeholder?: string | undefined;
  /** For a field holding a code rather than prose — a phone keyboard should show caps. */
  autocapitalize?: 'off' | 'none' | 'sentences' | 'words' | 'characters' | undefined;
  spellcheck?: boolean | undefined;
  /**
   * A `<textarea>` rather than an `<input>`, for a field that holds a paragraph.
   *
   * A prop here rather than a second component, because the label, the hint, the error and the
   * `aria-describedby` that ties them together are the whole reason this file exists — and a
   * hand-rolled textarea elsewhere is exactly the wiring that would be got wrong.
   */
  multiline?: boolean | undefined;
  rows?: number | undefined;
  /**
   * Keep the label for assistive technology but do not draw it.
   *
   * For a field whose section heading already says the same word — a "Your name" label under a
   * "Your name" heading is a heading printed twice, and the second one is not telling anybody
   * anything. The `<label for>` still exists, which is what the input is named by.
   */
  labelHidden?: boolean;
}>();

defineEmits<{ 'update:modelValue': [value: string] }>();

// useId, so two fields sharing a name still get distinct ids and their labels stay unambiguous.
const uid = useId();
const inputId = computed(() => `${props.name}-${uid}`);
const hintId = computed(() => `${inputId.value}-hint`);
const errorId = computed(() => `${inputId.value}-error`);

/*
 * Two sources rather than one flag, because the three ways to ask for this behave differently:
 * a mouse hovers and leaves, a keyboard focuses and blurs, and a phone — which has no hover at
 * all, and is where most of this app's forms are filled in — taps, and expects it to stay.
 */
/**
 * The input, for a caller that has to put the cursor in it — local play's "add somebody" opens a
 * field and focuses it, which is the whole point of the button.
 *
 * Exposed rather than left to a `ref` on the component: a component ref is the instance, not the
 * element, and reaching through `$el` to find the input is the kind of thing that breaks quietly
 * when this template grows a wrapper.
 */
const inputEl = ref<HTMLInputElement | HTMLTextAreaElement | null>(null);
defineExpose({ focus: () => inputEl.value?.focus() });

const hovered = ref(false);
const pinned = ref(false);
const hintShown = computed(() => !props.hintTooltip || hovered.value || pinned.value);

/**
 * Typing dismisses it.
 *
 * The popover hangs over the field it describes — it has to, or it would shove everything below
 * it down every time a mouse passed the "?" — and on a phone that is exactly where the input is.
 * A pinned hint would then cover what somebody had just typed, which a screenshot caught. Both
 * flags, because a tap can fire a synthetic `mouseenter` on the way to the input.
 */
function dismissHint(): void {
  hovered.value = false;
  pinned.value = false;
}

const describedBy = computed(() => {
  const ids = [props.hint ? hintId.value : null, props.error ? errorId.value : null].filter(
    (id): id is string => id !== null,
  );
  return ids.length > 0 ? ids.join(' ') : undefined;
});
</script>

<template>
  <div>
    <!-- `relative`, so the tooltip can hang under the "?" without moving the fields below it. -->
    <div class="relative flex items-center gap-1.5">
      <label :for="inputId" :class="labelHidden ? 'sr-only' : 'block text-sm font-medium'">
        {{ label }}
      </label>
      <!-- A real button, not an icon with a title attribute: a title is unreachable by keyboard
           and invisible on a phone, which are the two readers this is for. -->
      <button
        v-if="hint && hintTooltip"
        type="button"
        :aria-label="t('common.hintAria', { label })"
        :aria-expanded="hintShown"
        :aria-controls="hintId"
        class="-m-1 flex size-6 shrink-0 items-center justify-center rounded-full border border-line text-xs font-semibold text-ink-muted hover:border-accent hover:text-ink focus:outline-2 focus:outline-offset-2 focus:outline-focus"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
        @focus="hovered = true"
        @blur="hovered = false"
        @click="pinned = !pinned"
      >
        ?
      </button>
      <p
        v-if="hint && hintTooltip"
        :id="hintId"
        data-hint
        :class="
          hintShown
            ? 'absolute top-full left-0 z-10 mt-1 w-64 max-w-full rounded border border-line bg-surface p-2 text-sm text-ink-muted shadow-lg'
            : 'sr-only'
        "
      >
        {{ hint }}
      </p>
    </div>
    <p v-if="hint && !hintTooltip" :id="hintId" data-hint class="mt-1 text-sm text-ink-muted">
      {{ hint }}
    </p>
    <textarea
      v-if="multiline"
      :id="inputId"
      ref="inputEl"
      :name="name"
      :value="modelValue"
      :rows="rows ?? 5"
      :enterkeyhint="enterkeyhint"
      :maxlength="maxlength"
      :placeholder="placeholder"
      :spellcheck="spellcheck"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
      class="mt-1 w-full rounded border border-line bg-surface px-3 py-2 text-ink focus:outline-2 focus:outline-offset-2 focus:outline-focus"
      @focus="dismissHint"
      @input="$emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
    />
    <input
      v-else
      :id="inputId"
      ref="inputEl"
      :name="name"
      :value="modelValue"
      :type="type ?? 'text'"
      :inputmode="inputmode"
      :autocomplete="autocomplete"
      :enterkeyhint="enterkeyhint"
      :maxlength="maxlength"
      :placeholder="placeholder"
      :autocapitalize="autocapitalize"
      :spellcheck="spellcheck"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
      class="mt-1 w-full rounded border border-line bg-surface px-3 py-2 text-ink focus:outline-2 focus:outline-offset-2 focus:outline-focus"
      @focus="dismissHint"
      @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
    <!-- The word "Error" *and* a colour, plus aria-invalid on the input above: three cues, so
         this still works for somebody who cannot tell the red from the black. -->
    <p v-if="error" :id="errorId" class="mt-1 text-sm font-medium text-danger">
      {{ t('common.errorPrefix', { message: error }) }}
    </p>
  </div>
</template>
